import { useCallback, useMemo, useRef } from "react";

import { useStorage } from "./useStorage";

import type { MpxAgGridStateProps } from "./useMpxAgGridState";
import type { GridState, StateUpdatedEvent } from "ag-grid-community";

type PersistedAgGridView = Pick<GridState, "columnVisibility" | "filter">;

export interface UseAgGridViewStorageFacets {
  columnVisibility?: boolean;
  filter?: boolean;
}

export interface UseAgGridViewStorageConfig {
  /** Unique per grid+scope, e.g. `` `responses-grid-view:${ecoRfpId}` ``. */
  storageKey: string;
  /** The mpx-backed grid props to layer local persistence on top of. */
  gridProps: MpxAgGridStateProps;

  persist?: UseAgGridViewStorageFacets;
}

const DEFAULT_FACETS: Required<UseAgGridViewStorageFacets> = {
  columnVisibility: true,
  filter: true,
};

/**
 * Layers browser-local persistence of hidden columns and filters on top of
 * an mpx-backed `gridProps` (from `useMpxAgGridState`). Each call site
 * supplies its own `storageKey` so tables never collide. Returns a
 * drop-in replacement for `gridProps` — spread it onto the grid as usual.
 */
export const useAgGridViewStorage = ({
  storageKey,
  gridProps,
  persist,
}: UseAgGridViewStorageConfig): MpxAgGridStateProps => {
  const { columnVisibility: persistColumnVisibility, filter: persistFilter } = {
    ...DEFAULT_FACETS,
    ...persist,
  };
  const { getStorageItem, setStorageItem } = useStorage("local");
  const lastPersistedRef = useRef<PersistedAgGridView | null>(null);
  const {
    initialState: serverInitialState,
    onStateUpdated: serverOnStateUpdated,
  } = gridProps;

  const initialState = useMemo<GridState | undefined>(() => {
    const persisted = getStorageItem<PersistedAgGridView>(storageKey);
    if (persisted) {
      lastPersistedRef.current = persisted;
    }

    if (
      !serverInitialState &&
      (!persisted || Object.keys(persisted).length === 0)
    ) {
      return undefined;
    }

    return {
      ...serverInitialState,
      columnVisibility: persistColumnVisibility
        ? (persisted?.columnVisibility ?? serverInitialState?.columnVisibility)
        : undefined,
      filter: persistFilter
        ? (persisted?.filter ?? serverInitialState?.filter)
        : undefined,
    };
  }, [
    getStorageItem,
    storageKey,
    serverInitialState,
    persistColumnVisibility,
    persistFilter,
  ]);

  const onStateUpdated = useCallback(
    (event: StateUpdatedEvent) => {
      serverOnStateUpdated(event);

      if (event.sources?.includes("gridInitializing")) {
        return;
      }

      const state = event.api.getState();
      const next: PersistedAgGridView = {
        columnVisibility: persistColumnVisibility
          ? state.columnVisibility
          : undefined,
        filter: persistFilter ? state.filter : undefined,
      };
      const isUnchanged =
        JSON.stringify(lastPersistedRef.current) === JSON.stringify(next);
      if (isUnchanged) {
        return;
      }
      lastPersistedRef.current = next;
      setStorageItem(storageKey, next);
    },
    [
      serverOnStateUpdated,
      setStorageItem,
      storageKey,
      persistColumnVisibility,
      persistFilter,
    ],
  );

  return { key: gridProps.key, initialState, onStateUpdated };
};
